import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError
} from '@reduxjs/toolkit';
import { SERVER_REQUEST_STATUS } from 'src/enums';
import { ApiService } from 'src/services/api';
import { IFindRequestQuery, ServerRequestStatus } from 'src/types';
import { IDocument, IDocuments } from 'src/types/documents.types';

/*
|-----------------------------------------------------------------
| Initial State
|-----------------------------------------------------------------
|
*/

interface IDocumentsState {
  data: IDocuments | null;
  status: null | ServerRequestStatus;
  documentMarkAsReadStatus: null | ServerRequestStatus;
  query: null | IFindRequestQuery;
  error: SerializedError | null | string;
  documentMarkAsReadError: SerializedError | null | string;
  isShowImaging: boolean;
  isShowLabResults: boolean;
  isShowReleaseLetter: boolean;
  isShowEmergencyRoomSummary: boolean;
  isShowVisitSummary: boolean;
  isShowCurrentHospitalization: boolean;
  isShowMedicalHistory: boolean;
  isShowMedicalFileTab: boolean;
  isShowConsults: boolean;
  isShowEssentialSigns: boolean;
  isShowInstructionsForMedicine: boolean;
  isShowPermanentMedicine: boolean;
}

const initialState: IDocumentsState = {
  data: null,
  status: SERVER_REQUEST_STATUS.PENDING,
  documentMarkAsReadStatus: SERVER_REQUEST_STATUS.PENDING,
  query: null,
  error: null,
  documentMarkAsReadError: null,
  isShowImaging: false,
  isShowLabResults: false,
  isShowReleaseLetter: false,
  isShowEmergencyRoomSummary: false,
  isShowVisitSummary: false,
  isShowCurrentHospitalization: false,
  isShowMedicalHistory: false,
  isShowMedicalFileTab: false,
  isShowConsults: false,
  isShowEssentialSigns: false,
  isShowInstructionsForMedicine: false,
  isShowPermanentMedicine: false
};

/*
|-----------------------------------------------------------------
| Create async actions
|-----------------------------------------------------------------
|
*/
const onFetchDocumentsList = createAsyncThunk(
  'documents/onFetchDocumentsList ',
  async (query: { isPatient: boolean; unitCode: string }) => {
    console.log('in onFetchDocumentsList');
    let res;
    try {
      res = await ApiService.fetchDocumentsList(query);
    } catch (e) {
      console.log(e);
    }

    if (res == undefined) {
      return undefined;
    }

    return { data: res.data };
  }
);

// Async action to mark a document as read
export const markDocumentAsRead = createAsyncThunk(
  'documents/markDocumentAsRead',
  async (document: IDocument, { rejectWithValue }) => {
    try {
      const response = await ApiService.markDocumentAsRead(document); // Server request
      return response.data as IDocument; // Return the updated document
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to mark document as read'
      );
    }
  }
);

/*
|-----------------------------------------------------------------
| Create Slice
|-----------------------------------------------------------------
|
*/

const documentsState = createSlice({
  name: 'documentsState',
  initialState,
  reducers: {
    cleanState(state) {
      Object.assign(state, initialState);
    },
    setIsShowImaging(state, action: PayloadAction<boolean>) {
      state.isShowImaging = action.payload;
    },
    setIsShowLabResults(state, action: PayloadAction<boolean>) {
      state.isShowLabResults = action.payload;
    },
    setIsShowConsults(state, action: PayloadAction<boolean>) {
      state.isShowConsults = action.payload;
    },
    setIsShowEssentialSigns(state, action: PayloadAction<boolean>) {
      state.isShowEssentialSigns = action.payload;
    },
    setIsShowInstructionsForMedicine(state, action: PayloadAction<boolean>) {
      state.isShowInstructionsForMedicine = action.payload;
    },
    setIsShowPermanentMedicine(state, action: PayloadAction<boolean>) {
      state.isShowPermanentMedicine = action.payload;
    },
    setIsShowReleaseLetter(state, action: PayloadAction<boolean>) {
      state.isShowReleaseLetter = action.payload;
    },
    setIsShowEmergencyRoomSummary(state, action: PayloadAction<boolean>) {
      state.isShowEmergencyRoomSummary = action.payload;
    },
    setIsShowVisitSummary(state, action: PayloadAction<boolean>) {
      state.isShowVisitSummary = action.payload;
    },
    setIsShowCurrentHospitalization(state, action: PayloadAction<boolean>) {
      state.isShowCurrentHospitalization = action.payload;
    },
    setIsShowMedicalHistory(state, action: PayloadAction<boolean>) {
      state.isShowMedicalHistory = action.payload;
    },
    setIsShowMedicalFileTab(state, action: PayloadAction<boolean>) {
      state.isShowMedicalFileTab = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(onFetchDocumentsList.pending, (state) => {
        state.status = SERVER_REQUEST_STATUS.PENDING;
        state.error = null;
      })
      .addCase(onFetchDocumentsList.fulfilled, (state, action) => {
        state.status = SERVER_REQUEST_STATUS.FULFILLED;
        state.data = action.payload?.data;
      })
      .addCase(onFetchDocumentsList.rejected, (state, action) => {
        state.status = SERVER_REQUEST_STATUS.REJECTED;
        state.error = action.error;
      })
      .addCase(markDocumentAsRead.pending, (state, action) => {
        console.log('Pending action:', action.payload);
        state.documentMarkAsReadStatus = SERVER_REQUEST_STATUS.PENDING;
        state.documentMarkAsReadError = null;
      })
      .addCase(markDocumentAsRead.fulfilled, (state, action) => {
        const updatedDocument = action.payload;

        const updateList = (list: IDocument[]) =>
          list.map((doc) =>
            doc.docNum === updatedDocument.docNum
              ? { ...doc, isNew: updatedDocument.isNew }
              : doc
          );

        state.data = {
          ...state.data,
          LabDocList: updateList(state.data?.LabDocList || []),
          RadiologyDocList: updateList(state.data?.RadiologyDocList || []),
          ReleaseDocList: updateList(state.data?.ReleaseDocList || []),
          RepeatedErrors: state.data?.RepeatedErrors || [],
          WorstError: state.data?.WorstError || ''
        };
        state.documentMarkAsReadStatus = SERVER_REQUEST_STATUS.FULFILLED;
      })
      .addCase(markDocumentAsRead.rejected, (state, action) => {
        console.error('Error marking document as read:', action.payload);
        state.documentMarkAsReadStatus = SERVER_REQUEST_STATUS.REJECTED;
        state.documentMarkAsReadError = action.error;
      });
  }
});

/*
|-----------------------------------------------------------------
| Export actions & reducer
|-----------------------------------------------------------------
|
*/

export const documentsActions = {
  ...documentsState.actions,
  onFetchDocumentsList,
  markDocumentAsRead
};
export { default as documentsSelectors } from './documents.selectors';

export default documentsState.reducer;

export const setIsShowImaging = documentsState.actions.setIsShowImaging;
export const setIsShowLabResults = documentsState.actions.setIsShowLabResults;
export const setIsShowConsults = documentsState.actions.setIsShowConsults;
export const setIsShowEssentialSigns =
  documentsState.actions.setIsShowEssentialSigns;
export const setIsShowInstructionsForMedicine =
  documentsState.actions.setIsShowInstructionsForMedicine;
export const setIsShowPermanentMedicine =
  documentsState.actions.setIsShowPermanentMedicine;
export const setIsShowReleaseLetter =
  documentsState.actions.setIsShowReleaseLetter;
export const setIsShowVisitSummary =
  documentsState.actions.setIsShowVisitSummary;
export const setIsShowEmergencyRoomSummary =
  documentsState.actions.setIsShowEmergencyRoomSummary;

export const setIsShowCurrentHospitalization =
  documentsState.actions.setIsShowCurrentHospitalization;
export const setIsShowMedicalHistory =
  documentsState.actions.setIsShowMedicalHistory;

export const setIsShowMedicalFile =
  documentsState.actions.setIsShowMedicalFileTab;
